import InPlayTypes from './types.js';
import { uniqBy, pickBy } from 'mojito/utils';

const { ALL_SPORTS_CODE, FAVORITES, STREAMING } = InPlayTypes.TABS;

/**
 * Class containing utility methods related to event groups.
 *
 * @class InPlayUtils
 * @memberof Mojito.Modules.Inplay
 */
export default class InPlayUtils {
    /**
     * Retrieves the number of events in a group, given a map of event groups and an event group key.
     * If no event group key is specified, all events in all event groups are counted. Counts only unique events by event ID.
     *
     * @param {Array} eventGroups - Array of event groups.
     * @param {Function} [filter] - A predicate function to filter events in eventGroups.
     *
     * @returns {number} Returns the number of unique events in the group.
     *
     * @function Mojito.Modules.Inplay.utils.countEventsInEventGroups
     */
    static countEventsInEventGroups(eventGroups, filter) {
        // Sanity check
        if (!eventGroups) {
            return 0;
        }

        const allEventsInGroup = eventGroups.flatMap(group =>
            filter ? group.events.filter(filter) : group.events
        );

        return uniqBy(allEventsInGroup, event => event.id).length;
    }

    /**
     * Filters event groups based on user favorite events.
     *
     * @param {Array} [eventGroups = []] - Array of event groups.
     * @param {Array<string>} [favoriteEventIds = []] - Favorite event ids.
     *
     * @returns {Array} Returns filtered eventGroups with favorite events.
     *
     * @function Mojito.Modules.Inplay.utils.filterEventGroupsByFavoriteEvents
     */
    static filterEventGroupsByFavoriteEvents(eventGroups = [], favoriteEventIds = []) {
        return eventGroups
            .map(group => ({
                ...group,
                events: group.events.filter(event => favoriteEventIds.includes(event.id)),
            }))
            .filter(group => group.events.length > 0);
    }

    /**
     * Identify sport ids of favorite events.
     *
     * @param {object} [eventGroupsBySportMap = {}] - A map of event groups where key - is sportId, and eventGroups as a value.
     * @param {Array<string>} [favoriteEventIds = []] - Favorite events ids.
     *
     * @returns {Array<string>} Returns array of favorite events sport ids.
     *
     * @function Mojito.Modules.Inplay.utils.getFavoritizedSportIds
     */
    static getFavoritizedSportIds(eventGroupsBySportMap = {}, favoriteEventIds = []) {
        const favoriteGroupdBySportMap = pickBy(eventGroupsBySportMap, eventGroups => {
            const favoriteGroups = InPlayUtils.filterEventGroupsByFavoriteEvents(
                eventGroups,
                favoriteEventIds
            );
            return favoriteGroups.length > 0;
        });

        return InPlayUtils.omitStreamingId(Object.keys(favoriteGroupdBySportMap));
    }

    /**
     * Count events for specific sport group.
     *
     * @param {string} tabId - Tab id.
     * @param {object} [eventGroupsBySportMap] - A map of event groups where key - is tabId, and eventGroups as a value.
     * @param {Array<string>} [favoriteEventIds] - Favorite events ids.
     *
     * @returns {number} Returns the number of events in the specific sport group.
     *
     * @function Mojito.Modules.Inplay.utils.countEventsForInplayMenuTab
     */
    static countEventsForInplayMenuTab(tabId, eventGroupsBySportMap, favoriteEventIds) {
        const allEventGroups = Object.values(eventGroupsBySportMap).flat().filter(Boolean);
        if (tabId === ALL_SPORTS_CODE) {
            const allEventGroups = Object.values(eventGroupsBySportMap).flat().filter(Boolean);
            return InPlayUtils.countEventsInEventGroups(allEventGroups);
        }
        if (tabId === FAVORITES) {
            const allEventIds = allEventGroups.flatMap(group =>
                group.events.map(event => event.id)
            );
            const presentFavoriteIds = favoriteEventIds.filter(id => allEventIds.includes(id));
            return presentFavoriteIds.length;
        }

        const eventGroupsInTab = eventGroupsBySportMap[tabId];
        return InPlayUtils.countEventsInEventGroups(eventGroupsInTab);
    }

    /**
     * Removes streaming id from the list of sportIds.
     *
     * @param {Array<string>} sportIds - Sport ids.
     *
     * @returns {Array<string>} Returns array of sport ids without streaming.
     *
     * @function Mojito.Modules.Inplay.utils.omitStreamingId
     */
    static omitStreamingId(sportIds) {
        return sportIds.filter(sportId => sportId !== STREAMING);
    }
}
